/**
 * Focus window commands: lay the presented frames out, make one of them the
 * main frame, or a managed window (normal, maximized, snapped). Boxes come
 * from the planners in `lib/focus/`; the presentation's renderer draws them.
 */
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import type {
  FocusWindowBox,
  FocusWindowMode,
} from "@/lib/focus-window-layout";
import { rememberConversationCompanionFrameBox } from "@/lib/conversation-companion-focus-layout";
import { commitPresentation } from "@/lib/focus/focus-adapter";
import { currentFocusPlanEnv } from "@/lib/focus/focus-natural-size";
import { planFocusWindow } from "@/lib/focus/focus-plan-layout";
import type { PresentationState } from "@/lib/focus/focus-types";
import {
  planFocusLayout,
  planMakeMainFocusFrame,
  planPlaceRoundFrame,
  planToggleFocusWindow,
  withUserKept,
} from "@/lib/focus/focus-plan-rounds";

/**
 * The running focus, when its renderer draws this frame as a window: the
 * overlay draws every presented frame, the canvas those with a node.
 */
function managedFocus(frameId: string): PresentationState | null {
  const presentation = usePresentationStore.getState().presentation;
  return presentation?.variant === "focus" &&
    presentation.frameIds.includes(frameId) &&
    (presentation.renderer === "overlay" ||
      useCanvasStore.getState().nodes.some((node) => node.id === frameId))
    ? presentation
    : null;
}

/** Frame `frameId`'s managed window layout in the running focus. */
export function focusWindowForFrame(frameId: string) {
  const presentation = usePresentationStore.getState().presentation;
  return presentation?.variant === "focus" &&
    presentation.focusWindow?.frameId === frameId
    ? presentation.focusWindow
    : null;
}

export function isFocusWindowManagedFrame(frameId: string): boolean {
  return managedFocus(frameId) !== null;
}

function commitFocusChange(
  next: PresentationState | null,
  duration?: number,
): boolean {
  if (!next) return false;
  commitPresentation(next, { duration });
  return true;
}

/** Lay the running focus out again: given boxes, one window or rounds. */
export function applyFocusLayoutToFrames(
  activeId: string,
  options: {
    boxes?: FocusWindowBox[];
    duration?: number;
    preserveCustomFocusLayout?: boolean;
    focusWindowMode?: FocusWindowMode | null;
  } = {},
): boolean {
  const presentation = usePresentationStore.getState().presentation;
  if (presentation?.variant !== "focus") return false;
  return commitFocusChange(
    planFocusLayout(presentation, activeId, options, currentFocusPlanEnv()),
    options.duration,
  );
}

/**
 * "Show large": a side frame (or a placed window) becomes the main frame.
 * False when it already is, or its focus is a chat's layout.
 */
export function makeFocusFrameMain(frameId: string): boolean {
  const presentation = managedFocus(frameId);
  if (!presentation) return false;
  const next = planMakeMainFocusFrame(
    presentation,
    frameId,
    currentFocusPlanEnv(),
  );
  if (!next) return false;
  commitFocusChange(next, 250);
  useCanvasStore.getState().selectFrame(frameId);
  return true;
}

export function toggleFocusedFrameWindow(frameId: string): boolean {
  const presentation = managedFocus(frameId);
  if (!presentation) return false;
  return commitFocusChange(
    planToggleFocusWindow(presentation, frameId, currentFocusPlanEnv()),
    250,
  );
}

export function applyFocusedFrameWindowMode(
  frameId: string,
  mode: FocusWindowMode,
  options: { normalBox?: FocusWindowBox; duration?: number } = {},
): boolean {
  const presentation = managedFocus(frameId);
  if (!presentation) return false;
  return commitFocusChange(
    planFocusWindow(
      presentation,
      frameId,
      mode,
      { normalBox: options.normalBox },
      currentFocusPlanEnv(),
    ),
    options.duration,
  );
}

/**
 * The box a user dragged or resized a focused frame to: a companion stack
 * keeps it in its layout, rounds keep it as the user's placed window,
 * any other focus makes it a normal window there. The frame becomes the
 * user's: a new agent round keeps it (plan 13).
 */
export function placeFocusedFrameWindow(
  frameId: string,
  box: FocusWindowBox,
): boolean {
  const presentation = managedFocus(frameId);
  if (!presentation) return false;
  if (rememberConversationCompanionFrameBox(frameId, box)) return true;
  const env = currentFocusPlanEnv();
  if (presentation.customFocusLayout?.kind === "rounds") {
    return commitFocusChange(
      planPlaceRoundFrame(presentation, frameId, box, env),
      0,
    );
  }
  const next = planFocusWindow(
    presentation,
    frameId,
    "normal",
    { normalBox: box },
    env,
  );
  return commitFocusChange(next && withUserKept(next, frameId, box), 0);
}
